import { useEffect, useRef, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';

interface UseInactivityTimeoutOptions {
  /**
   * Timeout duration in minutes (default: 30)
   */
  timeoutMinutes?: number;

  /**
   * Warning duration in minutes before timeout (default: 2)
   */
  warningMinutes?: number;

  /**
   * Callback when warning is triggered
   */
  onWarning?: () => void;

  /**
   * Callback when timeout occurs
   */
  onTimeout?: () => void;

  /**
   * Whether the hook is enabled (default: true)
   * Set to false to disable timeout tracking
   */
  enabled?: boolean;
}

/**
 * Hook to automatically sign out users after a period of inactivity
 *
 * Features:
 * - Tracks mouse, keyboard, scroll, and touch activity
 * - Shows warning before timeout
 * - Automatically signs out inactive users
 * - Resets on any user activity
 *
 * @example
 * useInactivityTimeout({
 *   timeoutMinutes: 30,
 *   warningMinutes: 2,
 *   onWarning: () => showWarning('You will be signed out due to inactivity')
 * });
 */
export const useInactivityTimeout = (options: UseInactivityTimeoutOptions = {}) => {
  const {
    timeoutMinutes = 30,
    warningMinutes = 2,
    onWarning,
    onTimeout,
    enabled = true
  } = options;

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null); // ✅ FIX: Store throttle timeout in ref

  // Use refs for callbacks to avoid effect re-runs when they change
  const onWarningRef = useRef(onWarning);
  const onTimeoutRef = useRef(onTimeout);

  // Update refs when callbacks change
  useEffect(() => {
    onWarningRef.current = onWarning;
    onTimeoutRef.current = onTimeout;
  }, [onWarning, onTimeout]);

  // ✅ FIX: Remove clearTimers from dependency arrays to prevent effect re-runs
  const clearTimers = useCallback(() => {
    console.log('[InactivityTimeout] Clearing all timers');
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
  }, []);

  const handleTimeout = useCallback(async () => {
    // Check if user is still authenticated before timing out
    if (!auth.currentUser) {
      console.log('[InactivityTimeout] No user signed in, skipping timeout');
      return;
    }

    console.log('[InactivityTimeout] Timeout reached - user inactive for 60 minutes, signing out...');

    try {
      // Call timeout callback before signing out (for logging/cleanup)
      if (onTimeoutRef.current) {
        onTimeoutRef.current();
      }

      // Sign out the user
      await signOut(auth);
      console.log('[InactivityTimeout] User successfully signed out');
    } catch (error) {
      console.error('[InactivityTimeout] Error signing out:', error);
    }
  }, []);

  const handleWarning = useCallback(() => {
    // Check if user is still authenticated before showing warning
    if (!auth.currentUser) {
      console.log('[InactivityTimeout] No user signed in, skipping warning');
      return;
    }

    console.log('[InactivityTimeout] Warning - user will be signed out in 5 minutes due to inactivity');

    if (onWarningRef.current) {
      onWarningRef.current();
    }
  }, []);

  // ✅ FIX: Create stable resetTimeout function without circular dependencies
  // Use a ref to store the reset logic to avoid it being in effect dependencies
  const resetTimeoutLogic = useCallback((minutes: number, warningMins: number) => {
    const now = Date.now();
    lastActivityRef.current = now;

    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }

    // Set warning timer
    const warningMs = (minutes - warningMins) * 60 * 1000;
    warningRef.current = setTimeout(handleWarning, warningMs);

    // Set timeout timer
    const timeoutMs = minutes * 60 * 1000;
    timeoutRef.current = setTimeout(handleTimeout, timeoutMs);

    console.log(`[InactivityTimeout] Timers set at ${new Date().toLocaleTimeString()} - timeout in ${minutes}min, warning in ${minutes - warningMins}min`);
  }, [handleWarning, handleTimeout]);

  // Store reset function in ref for use in event handlers
  const resetTimeoutRef = useRef<(minutes: number, warningMins: number) => void>(resetTimeoutLogic);

  useEffect(() => {
    resetTimeoutRef.current = resetTimeoutLogic;
  }, [resetTimeoutLogic]);

  useEffect(() => {
    // Clear any existing timers immediately when disabled or no user
    if (!enabled || !auth.currentUser) {
      console.log('[InactivityTimeout] Disabled or no user authenticated, clearing timers');
      clearTimers();
      return;
    }

    console.log('[InactivityTimeout] Setting up inactivity tracking...');

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    // ✅ FIX: Throttled reset function that uses refs to access current config
    const throttledReset = () => {
      // Only reset if user is still authenticated
      if (!auth.currentUser) {
        console.log('[InactivityTimeout] User not authenticated, skipping activity reset');
        return;
      }

      // ✅ FIX: Use ref instead of local variable to persist across calls
      if (!throttleTimeoutRef.current) {
        throttleTimeoutRef.current = setTimeout(() => {
          // Use ref to get current timeout config (in case it changed)
          resetTimeoutRef.current(timeoutMinutes, warningMinutes);
          throttleTimeoutRef.current = null;
        }, 1000); // Max once per second
      }
    };

    // Reset inactivity timers when the tab becomes visible again. Don't
    // force-refresh the Firebase ID token — the SDK already refreshes when
    // the cached token is within 5 min of expiry, so a normal getIdToken()
    // call (e.g. the next API request) handles it for free. Forcing a
    // refresh on every visibility change burned a network round-trip per
    // tab focus.
    const handleVisibilityChange = () => {
      if (!document.hidden && auth.currentUser) {
        resetTimeoutRef.current(timeoutMinutes, warningMinutes);
      }
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledReset, { passive: true });
    });

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize timeout on mount
    console.log(`[InactivityTimeout] Initializing timers: ${timeoutMinutes}min timeout, ${warningMinutes}min warning`);
    resetTimeoutLogic(timeoutMinutes, warningMinutes);

    // Cleanup on unmount or when effect re-runs
    return () => {
      console.log('[InactivityTimeout] Cleaning up event listeners and timers');

      // Remove event listeners
      events.forEach(event => {
        window.removeEventListener(event, throttledReset);
      });

      // Remove visibility listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Clear all timers
      clearTimers();

      // Clear throttle timeout
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }
    };
    // ✅ FIX: Only depend on primitive values, not functions
    // This prevents unnecessary effect re-runs
  }, [enabled, timeoutMinutes, warningMinutes, resetTimeoutLogic, clearTimers]);

  // ✅ FIX: Provide manual reset function that uses current config
  const manualResetTimeout = useCallback(() => {
    console.log('[InactivityTimeout] Manual reset requested');
    resetTimeoutRef.current(timeoutMinutes, warningMinutes);
  }, [timeoutMinutes, warningMinutes]);

  return {
    /**
     * Manually reset the timeout (useful after important actions)
     */
    resetTimeout: manualResetTimeout,

    /**
     * Get the last activity timestamp
     */
    getLastActivity: () => lastActivityRef.current
  };
};
